// M1 (public site) — the owner's console for what visitors read on their site (galleries, projects, services, a team): a form over the
// fields the app's document names, a list of what is published, and a delete per row. It writes through
// /v1/apps/:appId/content/:collection (owner-only, server-checked against the same fields); a visitor reads the same rows with no account.
import { useCallback, useEffect, useState } from 'preact/hooks';
import type { PublicSubmitField } from '@vibeshell/shared';

type Row = { id: string; [k: string]: unknown };

export function ManageContent({ appId, collection, title, fields }: { appId: string; collection: string; title: string; fields: PublicSubmitField[] }) {
  const base = `/v1/apps/${encodeURIComponent(appId)}`;
  const [rows, setRows] = useState<Row[] | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${base}/public/${encodeURIComponent(collection)}`, { credentials: 'include' });
      setRows(r.ok ? ((await r.json()) as { items: Row[] }).items : []);
    } catch {
      setRows([]);
    }
  }, [base, collection]);
  useEffect(() => { void load(); }, [load]);

  const add = async (e: Event) => {
    e.preventDefault();
    setError('');
    const r = await fetch(`${base}/content/${encodeURIComponent(collection)}`, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ data: draft }) });
    if (!r.ok) { setError(r.status === 400 ? 'Please fill in the required fields.' : 'Could not save. Try again.'); return; }
    setDraft({});
    await load();
  };
  const remove = async (id: string) => {
    const r = await fetch(`${base}/content/${encodeURIComponent(collection)}/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) await load();
  };

  return (
    <section aria-label={title} data-content-collection={collection} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h3 style={{ margin: 0 }}>{title}</h3>
      <form onSubmit={add} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {fields.map((f) => (
          <label key={f.name} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>{f.label}{f.required ? ' *' : ''}</span>
            {f.type === 'longtext'
              ? <textarea name={f.name} rows={3} required={f.required} value={draft[f.name] ?? ''} onInput={(e) => setDraft({ ...draft, [f.name]: (e.target as HTMLTextAreaElement).value })} />
              : <input name={f.name} type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'} required={f.required} value={draft[f.name] ?? ''} onInput={(e) => setDraft({ ...draft, [f.name]: (e.target as HTMLInputElement).value })} />}
          </label>
        ))}
        <button type="submit">Add</button>
        {error ? <p role="alert">{error}</p> : null}
      </form>
      {rows === null ? <p>Loading…</p> : rows.length === 0 ? <p>Nothing published yet.</p> : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((row) => (
            <li key={row.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span>{String(row[fields[0]?.name ?? 'title'] ?? '')}</span>
              <button type="button" onClick={() => void remove(row.id)} aria-label={`Delete ${String(row[fields[0]?.name ?? 'title'] ?? '')}`}>Delete</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
